<script setup lang="ts">
import AlertComponent from "@/components/AlertComponent.vue";
import HasPermission from "@/components/HasPermission.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import ModalComponent from "@/components/ModalComponent.vue";
import ShadowBox from "@/components/ShadowBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledCheckbox from "@/components/StyledCheckbox.vue";
import StyledCombobox from "@/components/StyledCombobox.vue";
import useHasPermissionUser from "@/composables/useHasPermissionUser";
import useHotelForm from "@/internal/modules/providers/composables/useHotelForm";
import useHotels from "@/internal/modules/providers/composables/useHotels";
import useSearchHotels from "@/internal/modules/providers/composables/useSearchHotels";
import { Brand } from "@/internal/modules/providers/interfaces/Brand";
import { Hotel } from "@/internal/modules/providers/interfaces/Hotel";
import { computed, reactive, ref } from "vue";
import { useToast } from "vue-toastification";
import useBrandsHotel from "../composables/useBrandHotels";

interface Props {
  brand: Brand;
}

const { hasPermissionUser } = useHasPermissionUser();

const toast = useToast();

const props = defineProps<Props>();

const { data, isLoading, refetch, hotels } = useBrandsHotel({
  immediate: true,
  params: {
    id: props.brand.id,
  },
});

type Form = {
  hotels: Hotel[];
  brand: number;
};

const formHotels = reactive<Form>({
  hotels: [],
  brand: Number(props.brand.id),
});

const {
  data: hotelsSearch,
  debouncedSetQuery,
  isLoading: loadingHotels,
} = useSearchHotels({
  immediate: true,
  config: {
    errorHandling: false,
  },
});

const { updateHotelAsyncMutation, updateHotelLoading } = useHotels({
  immediate: false,
});

const { actionModal, clearState, modalAction } = useHotelForm();

const filteredHotels = computed(() => {
  return hotelsSearch.value?.data.filter((hotel) => {
    return hotel.brandId !== formHotels.brand;
  });
});

const selectedHotelId = ref<number | null>(null);

const alert = ref(false);
const alertMultiped = ref(false);

const updateBrandHotels = async () => {
  await Promise.all(
    formHotels.hotels.map((hotel: Hotel) =>
      updateHotelAsyncMutation({
        id: hotel.id,
        brandId: formHotels.brand,
      }),
    ),
  );

  await refetch();
  actionModal();
  formHotels.hotels = [];
};

const filteredHotelsChecked = computed(() => {
  return hotels.value.filter((hotel) => hotel.isChecked);
});

const selectedAll = () => {
  hotels.value.forEach((hotel) => {
    hotel.isChecked = !hotel.isChecked;
  });
};

const handleDeleteMassive = async () => {
  if (!filteredHotelsChecked.value.length) {
    toast.error("No hay marcas seleccionadas");
    return;
  }

  await Promise.all(
    filteredHotelsChecked.value.map(async (brand) => {
      await updateHotelAsyncMutation({
        id: brand.id,
        brandId: null,
      });
    }),
  );

  await refetch();
  alertMultiped.value = false;
};
</script>

<template>
  <ShadowBox class="bg-white">
    <div class="flex justify-between">
      <h1 class="font-bold text-2xl mb-2">Listado de hoteles</h1>

      <StyledButton
        class="bg-blue-500 text-white"
        @click="
          () => {
            actionModal();
          }
        "
        v-if="
          hasPermissionUser('update-internal', 'App\\Models\\Hotel') &&
          hasPermissionUser('viewAny-internal', 'App\\Models\\Hotel')
        "
      >
        <span class="material-symbols-outlined"> add </span>
        Agregar hotel
      </StyledButton>
    </div>

    <ul class="" v-if="!isLoading">
      <!-- Selection for hotels eliminate -->
      <HasPermission
        :entity-type="'App\\Models\\Hotel'"
        :name-permission="'update-internal'"
      >
        <div
          class="flex items-center mb-2 cursor-pointer"
          @click="selectedAll()"
          v-if="hotels.length > 0"
        >
          <span class="material-symbols-outlined"> done_all </span>
          <p>Seleccionar todos</p>
        </div>
      </HasPermission>

      <li v-for="hotel in hotels" :key="hotel.id">
        <div class="flex items-center">
          <!-- checkbox for select hotel -->

          <StyledCheckbox
            v-if="hasPermissionUser('update-internal', 'App\\Models\\Hotel')"
            v-model="hotel.isChecked"
            @update:model-value="
              (value) => {
                hotel.isChecked = value;
              }
            "
          />

          <!-- link for go to other hotel -->
          <p class="flex items-center gap-2 p-2 uppercase">
            <span class="material-symbols-outlined"> apartment </span>
            <RouterLink
              :to="{ name: 'hotels.hotel.edit', params: { id: hotel.id } }"
              class="flex items-center"
              :class="
                hasPermissionUser('viewAny-internal', 'App\\Models\\Hotel')
                  ? 'cursor-pointer underline hover:text-blue-700'
                  : 'cursor-default pointer-events-none no-underline'
              "
            >
              {{ hotel.name }}
            </RouterLink>
          </p>
        </div>
      </li>
    </ul>

    <!-- Action to eliminate massive hotels -->
    <div
      class="flex gap-5 items-center justify-end pt-2"
      v-if="filteredHotelsChecked.length > 0"
    >
      <StyledButton
        class="bg-red-500 text-white hover:bg-red-700"
        v-if="filteredHotelsChecked.length"
        @click="alertMultiped = true"
      >
        <span class="material-symbols-outlined"> delete </span>
        Eliminar marcas seleccionadas
      </StyledButton>

      <StyledButton
        class="bg-blue-500 text-white"
        @click="
          () => {
            selectedAll();
          }
        "
      >
        <span class="material-symbols-outlined"> undo </span>
        Deshacer selección
      </StyledButton>
    </div>
  </ShadowBox>

  <!-- Modal to eliminate massive hotels -->
  <AlertComponent
    v-model:model-value="alertMultiped"
    :type="'warning'"
    :dismiss="true"
    :on-confirm="handleDeleteMassive"
    :on-cancel="() => (alertMultiped = false)"
  >
    <template #title>
      Estas seguro de eliminar las marcas
      {{ filteredHotelsChecked.length > 1 ? "seleccionadas" : "seleccionada" }}:
      {{ filteredHotelsChecked.length }}
    </template>

    <template #body>
      <p v-for="brands in filteredHotelsChecked" :key="brands.id">
        {{ brands.name }}
      </p>
    </template>

    <template #closeButton> Cancelar </template>

    <template #button> Eliminar </template>
  </AlertComponent>

  <!-- Modal to create hotels -->
  <ModalComponent
    :model-value="modalAction"
    @update:model-value="
      () => {
        clearState();
        actionModal();
      }
    "
  >
    <template #modalBody>
      <div class="p-5">
        <form @submit.prevent="updateBrandHotels" class="">
          <h2 v-if="formHotels.hotels.length > 1" class="font-bold mb-2">
            Hoteles a ligar en el grupo {{ brand.name }}
          </h2>
          <div v-for="hotel in formHotels.hotels" :key="hotel.id">
            <div class="flex mb-1">
              <p
                class="p-2 bg-slate-400 rounded-md text-white hover:bg-slate-500 transition-colors flex items-center gap-2"
              >
                {{ hotel.name }}

                <span
                  class="material-symbols-outlined text-red-500 cursor-pointer"
                  v-tooltip="'Eliminar del grupo'"
                  @click="
                    () => {
                      formHotels.hotels = formHotels.hotels.filter(
                        (h) => h.id !== hotel.id,
                      );
                    }
                  "
                  >delete</span
                >
              </p>
            </div>
          </div>

          <StyledCombobox
            v-model="formHotels.hotels"
            multiple
            class="flex-1 mb-3"
            :options="filteredHotels || []"
            :loading-options="loadingHotels"
            label="Buscar hoteles"
            :on-input-change="debouncedSetQuery"
          >
            <template #default="{ option }">
              <span>{{ option.name }}</span>
            </template>
          </StyledCombobox>

          <StyledButton
            type="submit"
            class="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded ml-auto"
            :loading="updateHotelLoading"
          >
            Ligar hoteles
          </StyledButton>
        </form>
      </div>
    </template>
  </ModalComponent>

  <LoadingModal :when="isLoading" />
</template>
